import styles from './Promos.module.scss';
import parse from 'html-react-parser';
import {
  EBaseButtonSize,
  EBaseButtonType,
  EButtonBorderRadiusStrength,
  EButtonTextAlign,
} from '@spinwise/react-component-library/ButtonAbstract';
import { t } from 'i18next';
import { EBrandTextColor } from 'Libraries/typography.library';
import { useDispatch, useSelector } from 'react-redux';
import { IApplicationState, OVERLAY_PROMOTION_SHOW } from '@spinwise/spinwise-core';
import { EPromoSlugs } from 'Types/globals';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { IconLibrary } from 'Libraries/icons.library';
import { IconVariantsLibrary } from 'Libraries/variants.library';
import { EBrandButtonTypes } from 'Libraries/buttons.library';
import classNames from 'classnames';
import * as gtag from './../../../../lib/gtag';
import Loading from 'Components/shared/Loading/Loading';
import { ETitleBarHierarchy, TitleBar } from '@spinwise/react-component-library/TitleBar';
import { EComponentVisibility } from '@spinwise/react-component-library/Global';
import { IconsButton } from '@spinwise/react-component-library/ButtonIcons';
import { ButtonTextOnly } from '@spinwise/react-component-library/ButtonTextOnly';
import { EBaseTextWeight } from '@spinwise/react-component-library/BaseText';

interface IPromotionProps {
  data: any;
  isLoading: boolean;
}

enum EPromotionCategories {
  All = 'all',
  Casino = 'casino',
  Sport = 'sport',
}

const PromosComponent = ({ data, isLoading }: IPromotionProps) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { query = null } = router;
  const [category, setCategory] = useState(EPromotionCategories.All);

  useEffect(() => {
    if (query && query.page && data) {
      const promo = data?.find(promo => promo.attributes.slug === query.page);

      if (promo) {
        handleModalClick(promo);
      }
    }
  }, [query?.page, data]);

  useEffect(() => {
    if (query && query.category) {
      if (Object.values(EPromotionCategories).includes(query.category as EPromotionCategories)) {
        setCategoryState(query?.category as EPromotionCategories);
      }
    }
  }, []);

  const setCategoryState = (value: EPromotionCategories) => {
    if (category == value) return;

    setCategory(value);

    if (value == EPromotionCategories.Sport)
      gtag?.event?.('sportsbook-promotions-opened', {
        category: 'Pages',
        label: 'visited promotions page',
      });
  };

  const depositCount = useSelector((state: IApplicationState) => state.accountReducer?.info?.depositCount);
  const { auth } = useSelector((state: IApplicationState) => state.authReducer);

  const handleModalClick = bonus => {
    if (router?.query?.page !== bonus.attributes?.slug) {
      router.push({ query: { ...router.query, page: bonus.attributes?.slug } }, null, { shallow: true });
    }

    dispatch({
      type: OVERLAY_PROMOTION_SHOW,
      content: {
        show: true,
        content: bonus,
      },
    });
  };

  const categories = [
    { title: t('promo.category.all'), category: EPromotionCategories.All, iconName: IconLibrary.Loyalty },
    { title: t('promo.category.casino'), category: EPromotionCategories.Casino, iconName: IconLibrary.Game },
    { title: t('promo.category.sport'), category: EPromotionCategories.Sport, iconName: IconLibrary.Ball },
  ];

  return (
    <>
      <div
        className={`${styles.promotion_container} ${auth?.success ? styles.promotion_container__authenticated : ''}`}
      >
        <div className={classNames(styles.offers_container)}>
          <div className={styles.titlebar_wrapper}>
            <TitleBar
              hierarchy={ETitleBarHierarchy.Primary}
              title={t('promotions.main.title')}
              iconLeading={false}
              iconTrailing={false}
            />
          </div>
          <div
            className={classNames(EComponentVisibility.TableteDesktop, styles.offers_container__switcher, 'd-fl jc-sb')}
          >
            {categories.map((el, i) => {
              return (
                <div className={styles.offers_container__switcher_item} key={el.title}>
                  <IconsButton
                    onClick={() => setCategoryState(el.category)}
                    iconLeft={{
                      iconName: el.iconName,
                      variant:
                        category === el.category ? IconVariantsLibrary.Black08 : IconVariantsLibrary.PromotionOffer,
                    }}
                    iconRight={undefined}
                    label={el.title}
                    size={EBaseButtonSize.Large}
                    type={EBrandButtonTypes.PromotionSwitcher}
                    borderRadius={EButtonBorderRadiusStrength.Three}
                    isActive={category === el.category}
                    activeType={EBrandButtonTypes.PromotionSwitcherActive}
                    fullWidth={true}
                  ></IconsButton>
                </div>
              );
            })}
          </div>
          <div className={classNames(EComponentVisibility.MobileOnly, styles.offers_container__switcher, 'd-fl jc-sb')}>
            {categories.map((el, i) => {
              return (
                <div className={styles.offers_container__switcher_item} key={el.title}>
                  <ButtonTextOnly
                    onClick={() => setCategoryState(el.category)}
                    label={el.title}
                    size={EBaseButtonSize.Large}
                    type={EBrandButtonTypes.PromotionSwitcher}
                    borderRadius={EButtonBorderRadiusStrength.Three}
                    isActive={category === el.category}
                    activeType={EBrandButtonTypes.PromotionSwitcherActive}
                    fullWidth={true}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <Loading isLoading={isLoading} />
        {data &&
          data?.map?.(promo => {
            if (auth.success && promo.attributes.slug === EPromoSlugs.WELCOME_PACK && depositCount > 2) {
              return null;
            }
            if (promo && (category === EPromotionCategories.All || promo?.attributes?.category === category)) {
              return (
                <div key={promo.id} className={styles.bonus} onClick={() => handleModalClick(promo)}>
                  <div className={`${EComponentVisibility.TableteDesktop}`}>
                    <img
                      src={promo?.attributes?.image?.data?.attributes?.url}
                      alt={
                        promo?.attributes?.preview_title ? promo?.attributes?.preview_title : promo?.attributes?.title
                      }
                      className={styles.promo_img}
                    />
                  </div>
                  <div className={`${EComponentVisibility.MobileOnly}`}>
                    <img
                      src={
                        promo?.attributes?.preview_image_mobile?.data?.attributes?.url
                          ? promo?.attributes?.preview_image_mobile?.data?.attributes?.url
                          : promo?.attributes?.image?.data?.attributes?.url
                      }
                      alt={
                        promo?.attributes?.preview_title ? promo?.attributes?.preview_title : promo?.attributes?.title
                      }
                      className={`${styles.promo_img}`}
                    />
                  </div>
                  <div className={styles.bonus_content}>
                    <h2 className={styles.bonus_content__title}>
                      {promo?.attributes?.preview_title ? promo?.attributes?.preview_title : promo?.attributes?.title}
                    </h2>
                    <div className={styles.bonus_content__description}>
                      {promo?.attributes?.preview_description
                        ? parse(promo?.attributes?.preview_description)
                        : parse(promo?.attributes?.description)}
                    </div>
                  </div>
                  <div className={`${styles.bonus_btn}`}>
                    <ButtonTextOnly
                      alignText={EButtonTextAlign.Left}
                      borderRadius={EButtonBorderRadiusStrength.Three}
                      size={EBaseButtonSize.Large}
                      fullWidth={false}
                      type={EBaseButtonType.None}
                      textWeight={EBaseTextWeight.W400}
                      textColor={EBrandTextColor.Violet}
                      label={t('bonuses.read.more.btn')}
                    />
                  </div>
                </div>
              );
            }
          })}
      </div>
    </>
  );
};
export default PromosComponent;
